import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, DollarSign, Smartphone, CreditCard } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

function StripeTiersSection({ packages, setPackages, onSave, saving }) {
  const updatePackage = (id, field, value) => {
    setPackages(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: field === "amount" ? parseFloat(value) || 0 : value }
    }));
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-stripe-tiers">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={18} className="text-[#E8922F]" />
        <h4 className="font-semibold text-[#1A1A1A]">Card Donation Tiers</h4>
      </div>
      <p className="text-[#4A4A4A] text-xs mb-4">Edit the name, amount (USD), and description for each tier.</p>
      <div className="space-y-4">
        {Object.entries(packages).map(([id, pkg]) => (
          <div key={id} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end border-b border-gray-50 pb-4">
            <div>
              <Label className="text-xs text-gray-500">Name</Label>
              <Input value={pkg.name || ""} onChange={e => updatePackage(id, "name", e.target.value)} className="rounded-lg mt-1 text-sm" data-testid={`pkg-name-${id}`} />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Amount ($)</Label>
              <Input type="number" step="0.01" value={pkg.amount || ""} onChange={e => updatePackage(id, "amount", e.target.value)} className="rounded-lg mt-1 text-sm" data-testid={`pkg-amount-${id}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-gray-500">Description</Label>
              <Input value={pkg.description || ""} onChange={e => updatePackage(id, "description", e.target.value)} className="rounded-lg mt-1 text-sm" data-testid={`pkg-desc-${id}`} />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={onSave} className="btn-primary mt-4 text-sm" disabled={saving} data-testid="save-packages-btn">
        <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Tiers"}
      </Button>
    </div>
  );
}

function MobileMoneySection({ mobileMoney, setMobileMoney, onSave, saving }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-mobile-money">
      <div className="flex items-center gap-2 mb-4">
        <Smartphone size={18} className="text-[#E8922F]" />
        <h4 className="font-semibold text-[#1A1A1A]">Mobile Money Details</h4>
      </div>
      <p className="text-[#4A4A4A] text-xs mb-4">Edit the phone numbers shown to donors for Mobile Money payments.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs text-gray-500">MTN Mobile Money Number</Label>
          <Input value={mobileMoney.mtn_number} onChange={e => setMobileMoney({...mobileMoney, mtn_number: e.target.value})} className="rounded-lg mt-1 text-sm" placeholder="+256 7XX XXX XXX" data-testid="mtn-number-input" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Airtel Money Number</Label>
          <Input value={mobileMoney.airtel_number} onChange={e => setMobileMoney({...mobileMoney, airtel_number: e.target.value})} className="rounded-lg mt-1 text-sm" placeholder="+256 7XX XXX XXX" data-testid="airtel-number-input" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Registered Name</Label>
          <Input value={mobileMoney.registered_name} onChange={e => setMobileMoney({...mobileMoney, registered_name: e.target.value})} className="rounded-lg mt-1 text-sm" data-testid="mobile-name-input" />
        </div>
      </div>
      <Button onClick={onSave} className="btn-primary mt-4 text-sm" disabled={saving} data-testid="save-mobile-btn">
        <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Mobile Money"}
      </Button>
    </div>
  );
}

function PaypalSection({ paypalLink, setPaypalLink, onSave, saving }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="manage-paypal">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign size={18} className="text-[#E8922F]" />
        <h4 className="font-semibold text-[#1A1A1A]">PayPal Link</h4>
      </div>
      <p className="text-[#4A4A4A] text-xs mb-4">Enter your PayPal.me link or donation page URL.</p>
      <Input value={paypalLink} onChange={e => setPaypalLink(e.target.value)} className="rounded-lg text-sm" placeholder="https://paypal.me/yourname" data-testid="paypal-link-input" />
      <Button onClick={onSave} className="btn-primary mt-4 text-sm" disabled={saving} data-testid="save-paypal-btn">
        <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save PayPal"}
      </Button>
    </div>
  );
}

export default function DonationsManager({ siteSettings, loadData }) {
  const [packages, setPackages] = useState({});
  const [mobileMoney, setMobileMoney] = useState({ mtn_number: "", airtel_number: "", registered_name: "" });
  const [paypalLink, setPaypalLink] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => {
    if (siteSettings.donation_packages) setPackages(siteSettings.donation_packages);
    if (siteSettings.mobile_money) setMobileMoney(siteSettings.mobile_money);
    if (siteSettings.paypal_link) setPaypalLink(siteSettings.paypal_link);
  }, [siteSettings]); // eslint-disable-line react-hooks/exhaustive-deps -- setters are stable

  const saveSection = async (key, data) => {
    setSaving(key);
    try {
      await axios.put(`${API}/settings`, { [key]: data }, { withCredentials: true, headers: authHeaders() });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="space-y-8">
      <StripeTiersSection packages={packages} setPackages={setPackages} onSave={() => saveSection("donation_packages", packages)} saving={saving === "donation_packages"} />
      <MobileMoneySection mobileMoney={mobileMoney} setMobileMoney={setMobileMoney} onSave={() => saveSection("mobile_money", mobileMoney)} saving={saving === "mobile_money"} />
      <PaypalSection paypalLink={paypalLink} setPaypalLink={setPaypalLink} onSave={() => saveSection("paypal_link", paypalLink)} saving={saving === "paypal"} />
    </div>
  );
}
